import { Elements, PaymentElement } from "@stripe/react-stripe-js";
import type { PaymentIntent, StripeError } from "@stripe/stripe-js";
import type { FC } from "react";
import { useCallback, useMemo } from "react";

import { Button } from "@/components/flexkit/Button";
import { useToast } from "@/components/flexkit/toast";
import { cn } from "@/components/podkit/lib/cn";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { getStripeAppearance, useStripePromise } from "@/hooks/use-stripe";
import { useTheme } from "@/hooks/use-theme";
import { useConfirmStripePayment } from "@/queries/organization-queries";
import { formatError } from "@/utils/errors";

type StripePaymentFormProps = {
    clientSecret: string;
    onSuccess: (paymentIntent: PaymentIntent) => void;
    onError: (error: StripeError | Error) => void;

    "data-tracking-id": string;
    submitButtonText?: string;
    submitButtonClassName?: string;
    className?: string;
};
export const StripePaymentForm: FC<StripePaymentFormProps> = ({
    clientSecret,
    onSuccess,
    onError,
    submitButtonText = "Continue",
    submitButtonClassName = "",
    className = "",
    "data-tracking-id": dataTrackingId,
}) => {
    const { stripePromise } = useStripePromise();
    const theme = useTheme();

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const appearance = useMemo(() => getStripeAppearance(), [theme]);

    if (!stripePromise) {
        return <LoadingState />;
    }

    return (
        <div className={className}>
            <Elements stripe={stripePromise} options={{ clientSecret, appearance, loader: "always" }}>
                <PaymentForm
                    onSuccess={onSuccess}
                    onError={onError}
                    submitButtonText={submitButtonText}
                    submitButtonClassName={submitButtonClassName}
                    data-tracking-id={dataTrackingId}
                />
            </Elements>
        </div>
    );
};

type PaymentFormProps = {
    onSuccess: (paymentIntent: PaymentIntent) => void;
    onError: (error: StripeError | Error) => void;
    submitButtonText: string;
    submitButtonClassName: string;
    "data-tracking-id": string;
};
const PaymentForm: FC<PaymentFormProps> = ({
    onSuccess,
    onError,
    submitButtonText,
    submitButtonClassName,
    "data-tracking-id": dataTrackingId,
}) => {
    const { toast } = useToast();
    const confirmPaymentMutation = useConfirmStripePayment();

    const handleStripeLoadError = useCallback(
        (event: { elementType: "payment"; error: StripeError }) => {
            toast({
                title: "Failed to load Stripe",
                description: formatError(event.error),
            });
        },
        [toast],
    );

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        confirmPaymentMutation.mutate(undefined, {
            onSuccess: (paymentIntent) => {
                onSuccess(paymentIntent);
            },
            onError: (error) => {
                console.error("Payment setup error:", error);
                onError(error as StripeError | Error);
            },
        });
    };

    return (
        <form onSubmit={handleSubmit} className="w-full">
            <div className="w-full">
                <PaymentElement
                    id="payment-element"
                    onLoadError={handleStripeLoadError}
                    options={{
                        wallets: {
                            link: "never",
                        },
                        fields: {
                            billingDetails: {
                                name: "auto",
                                address: {
                                    country: "auto",
                                    postalCode: "auto",
                                },
                            },
                        },
                    }}
                />
            </div>
            <Button
                type="submit"
                disabled={confirmPaymentMutation.isPending}
                loading={confirmPaymentMutation.isPending}
                variant="primary"
                size="lg"
                className={cn("mt-8 w-full", submitButtonClassName)}
                data-tracking-id={dataTrackingId}
            >
                {submitButtonText}
            </Button>
        </form>
    );
};
